/**
 * Offline sync API contract (Phase 16C, backend plan 16B B3 / B10, design §6.6 / §8.6).
 *
 * POST /patientmr/sync (as built in 16B)
 *   headers: X-Device-Class: MOBILE, X-Pending-Ops (count of unsynced ops)
 *   body:    { deviceId, ops: SyncOpDto[] }                  plaintext, enrolled non-revoked device
 *            { deviceId, sealed: true, ops: SealedOpDto[] }  escrow copies (allowed after revocation)
 *   200:     { results: SyncOpResultDto[], deferred?: opId[], revoked?: boolean, serverTime? }
 *   4xx:     403 OFFLINE_MOBILE_ONLY | OFFLINE_DEVICE_REVOKED | OP_USER_MISMATCH;
 *            400 BATCH_TOO_LARGE | PAYLOAD_TOO_LARGE; 409 OP_IN_FLIGHT (retry later); 503 (backoff)
 *
 * CHART_UPDATE / ASSESSMENT_UPDATE carry `expectedVersion`, compared with the server's
 * `editVersion` (not `version`, which is the amendment / reopen number shown in the UI).
 * Payloads are strict: only the fields the online endpoint accepts (else VALIDATION + error.field).
 *
 * Every shape the backend owns is isolated here so a contract change touches one file.
 */

export type SyncOpType =
  | 'VISIT_START'
  | 'VISIT_END'
  | 'VISIT_CANCEL'
  | 'CHART_CREATE'
  | 'CHART_UPDATE'
  | 'CHART_SUBMIT'
  | 'ASSESSMENT_CREATE'
  | 'ASSESSMENT_UPDATE'
  | 'ASSESSMENT_COMPLETE';

export interface TimeAnchorDto {
  /** Last server Date header seen by this device */
  serverAt: string;
  /** Device clock at that moment */
  deviceAt: string;
}

/** One operation as sent to the server (also the plaintext inside a sealed escrow copy). */
export interface SyncOpDto {
  opId: string;
  type: SyncOpType;
  userId: string;
  tenantId: string;
  patientId: string;
  visitId: string;
  entityId?: string;
  dependsOn?: string;
  seq: number;
  capturedAt: string;
  monotonicMs: number;
  timeAnchor?: TimeAnchorDto;
  expectedVersion?: number;
  observedAt?: string;
  /** The same body the online endpoint accepts; blobs are inlined (e.g. `signature` data URL) */
  payload: Record<string, unknown>;
}

export interface SyncRequestDto {
  deviceId: string;
  ops: SyncOpDto[];
}

/** Escrow copy: ECDH P-256 (ephemeral) → HKDF-SHA256 (salt opId, info pelora-escrow-v1) → AES-256-GCM, AAD `${opId}:${tenantId}`. */
export interface SealedOpDto {
  opId: string;
  kid: string;
  /** Ephemeral public key, SPKI, base64 */
  epk: string;
  /** 12-byte IV, base64 */
  iv: string;
  /** Ciphertext with the GCM tag, base64 */
  ct: string;
}

export interface SealedSyncRequestDto {
  deviceId: string;
  sealed: true;
  ops: SealedOpDto[];
}

/** 'quarantined' is accepted defensively; 16B reports it as `quarantined: true` on needs_attention. */
export type SyncResultStatus = 'applied' | 'duplicate' | 'conflict' | 'needs_attention' | 'quarantined';

/** Codes the client acts on (others are shown as they come). */
export type SyncResultCode = string;

export const SYNC_CODES = {
  INVALID_TRANSITION: 'INVALID_TRANSITION',
  QUARANTINED: 'QUARANTINED',
  DEPENDENCY_FAILED: 'DEPENDENCY_FAILED',
  VALIDATION: 'VALIDATION',
} as const;

export interface SyncOpResultDto {
  opId: string;
  status: SyncResultStatus;
  /** Server ids of what the op created, e.g. { visitId } or { entryId } */
  serverIds?: Record<string, string>;
  version?: number;
  /** The entity's edit version after the op (the next expectedVersion), when returned */
  editVersion?: number;
  /** Held server-side for supervisor import (e.g. TIME_TOO_OLD / TIME_IN_FUTURE / unassigned) */
  quarantined?: boolean;
  /** Business-rule flags recorded on the visit (PATIENT_ON_HOLD, OVERLAP, SHIFT_UNMATCHED, ...) */
  syncFlags?: string[];
  error?: { code: SyncResultCode; message?: string; field?: string } | SyncResultCode;
  /** The entity as stored on the server (conflicts) */
  current?: Record<string, unknown> | null;
}

export interface SyncResponseDto {
  results: SyncOpResultDto[];
  /** Ops not reached within the server time budget: resend */
  deferred?: string[];
  /** The device was revoked: upload sealed copies, then wipe */
  revoked?: boolean;
  /** Server clock, used when the Date header is not readable cross-origin */
  serverTime?: string;
}

/** Tenant escrow public key, delivered with the visit packets. */
export interface EscrowKeyDto {
  kid: string;
  /** P-256 public key, SPKI, base64 */
  spki: string;
}
